/**
 * Las tarjetas de ratio como DATOS, compartidas por «KPIs financieros» y por Pérdidas y ganancias:
 * qué ratio es, con qué formato se pinta y qué variables del PGC lo componen (las claves del
 * desglose de cuentas del panel de detalle).
 */
import type { TFunction } from 'i18next'
import { ratioMetaFor } from '@jr2os/shared'
import type { AccountRow, BalanceKpisBlock, PnlKpisBlock, Ratio } from '@jr2os/shared'
import { ratioInsightKey, type RatioKey } from '../../../lib/ratio-insight'
import type { DrilldownGroup, KpiDetail } from '../KpiDrilldownDrawer'
import { formatRatio, type RatioFormat } from './kpi-format'

export interface KpiCardDef {
  key: string
  ratio: Ratio
  format: RatioFormat
  /** Variables del PGC que componen el ratio (claves de drilldown). */
  drilldownKeys: string[]
}

/**
 * Los ratios de balance. La prueba ácida solo si hay existencias que descontar (ver `ratioMetaFor`
 * en @jr2os/shared, que es también la lista del informe PDF).
 */
export function balanceCardDefs(balance: BalanceKpisBlock): KpiCardDef[] {
  return ratioMetaFor('balance', balance.vars.EX !== 0).map((m) => ({
    key: m.key,
    ratio: balance.ratios[m.key as keyof BalanceKpisBlock['ratios']],
    format: m.format,
    drilldownKeys: [...m.drilldownKeys],
  }))
}

/** Los ratios de la cuenta de resultados. */
export function pnlCardDefs(pnl: PnlKpisBlock): KpiCardDef[] {
  return ratioMetaFor('pnl', false).map((m) => ({
    key: m.key,
    ratio: pnl.ratios[m.key as keyof PnlKpisBlock['ratios']],
    format: m.format,
    drilldownKeys: [...m.drilldownKeys],
  }))
}

/**
 * Lo que enseña el panel de detalle al pulsar una tarjeta. Las cuentas van agrupadas por variable
 * (AC, PC…): así se ve de dónde sale cada lado de la fórmula, con su subtotal, en vez de una lista
 * plana que mezcla activo y pasivo.
 */
export function cardDrill(
  card: KpiCardDef,
  drilldown: Record<string, AccountRow[]>,
  t: TFunction<'accounting'>
): { title: string; groups: DrilldownGroup[]; detail: KpiDetail } {
  const groups: DrilldownGroup[] = card.drilldownKeys.map((key) => ({
    key,
    accounts: drilldown[key] ?? [],
  }))
  const ratioKey = card.key as RatioKey
  return {
    title: card.ratio.label,
    groups,
    detail: {
      ratio: card.ratio,
      ratioKey,
      format: card.format,
      valueText: formatRatio(card.ratio.value, card.format),
      why: t(`insight.${ratioKey}.${ratioInsightKey(ratioKey, card.ratio.value)}`, {
        defaultValue: '',
      }),
    },
  }
}
